import GroupLayer from '@arcgis/core/layers/GroupLayer';
import Layer from '@arcgis/core/layers/Layer';
import WFSLayer from '@arcgis/core/layers/WFSLayer';
import { COMPANION_FOOTPRINT_TITLE } from '../companionLayerTitles';
import { GISServiceTypes } from '../gisServicesTypes';
import { ensureLoadedLayerArray, LayerLoader } from './LayerLoader';
import { createCompanionRasterLayers } from './createCompanionRasterLayers';
import { resolveAdminOpacity } from './defaultLayerLoader';
import { parseOgcServiceUrl } from './parseOgcServiceUrl';
import { resolveRasterCompanion } from './resolveRasterCompanion';

function resolveFeatureTypeNames(
  sublayers: string[] | undefined,
  urlFeatureType?: string,
): string[] {
  const fromAttributes = (sublayers || [])
    .map(name => name?.trim())
    .filter((name): name is string => Boolean(name));

  if (fromAttributes.length > 0) {
    return [...new Set(fromAttributes)];
  }

  const fromUrl = urlFeatureType?.trim();
  return fromUrl ? [fromUrl] : [];
}

function createWfsLayers(args: {
  featureTypeNames: string[];
  url: string;
  customParameters?: Record<string, string>;
  gisLayerID: string;
  displayName?: string;
  opacity?: number;
  /** When true, use Footprint labels instead of vendor/feature-type names. */
  withRasterCompanion?: boolean;
}): WFSLayer[] {
  const {
    featureTypeNames,
    url,
    customParameters,
    gisLayerID,
    displayName,
    opacity,
    withRasterCompanion,
  } = args;

  return featureTypeNames.map((featureTypeName, index) => {
    const isOnly = featureTypeNames.length === 1;
    const title = withRasterCompanion
      ? isOnly
        ? COMPANION_FOOTPRINT_TITLE
        : `${COMPANION_FOOTPRINT_TITLE} ${index + 1}`
      : isOnly
        ? displayName || featureTypeName || 'WFS'
        : featureTypeName;

    return new WFSLayer({
      url,
      id: isOnly ? `${gisLayerID}-wfs` : `${gisLayerID}-wfs-${index}`,
      title,
      name: featureTypeName,
      visible: true,
      ...(customParameters ? { customParameters } : {}),
      ...(opacity !== undefined ? { opacity } : {}),
    });
  });
}

/**
 * Load a WFS service (one or more feature types). When Andromeda sets
 * `attributes.raster_companion.enabled` with an explicit WMS/WMTS URL +
 * layer name(s), also attach those raster layers under a GroupLayer.
 */
export const wfsLayerLoader: LayerLoader = {
  shouldUseLoader(service) {
    return service.service_type == GISServiceTypes.wfs;
  },

  async load(service) {
    const adminOpacity = resolveAdminOpacity(service);
    const {
      url,
      customParameters,
      featureTypeName: urlFeatureType,
    } = parseOgcServiceUrl(service.gis_service_url);

    const featureTypeNames = resolveFeatureTypeNames(
      service?.attributes?.sublayers,
      urlFeatureType,
    );

    if (featureTypeNames.length === 0) {
      // eslint-disable-next-line no-console
      console.error('WFS service is missing feature type name(s):', service);
      return [];
    }

    const companion = resolveRasterCompanion({
      rasterCompanion: service.attributes?.raster_companion,
      wfsCustomParameters: customParameters,
    });

    const wfsLayers = createWfsLayers({
      featureTypeNames,
      url,
      customParameters,
      gisLayerID: service.gisLayerID,
      displayName: service.display_name,
      opacity: adminOpacity,
      withRasterCompanion: !!companion,
    });

    const rasterLayers = companion
      ? createCompanionRasterLayers(companion, {
          id: `${service.gisLayerID}-raster`,
          opacity: adminOpacity,
        })
      : [];

    const needsGroup = rasterLayers.length > 0 || wfsLayers.length > 1;

    if (!needsGroup) {
      const [only] = wfsLayers;
      only.id = service.gisLayerID;
      await only.load();
      return ensureLoadedLayerArray(only);
    }

    // GroupLayer draws first→bottom, last→top. Keep WFS footprints under the
    // raster companion so floor-plan imagery is not covered by the outline.
    const childLayers: Layer[] = [...wfsLayers, ...rasterLayers];

    const group = new GroupLayer({
      id: service.gisLayerID,
      title: service.display_name || featureTypeNames[0],
      visible: false,
      layers: childLayers,
    });

    await group.load();
    await Promise.all(childLayers.map(layer => layer.load()));

    return ensureLoadedLayerArray(group);
  },
};
